import { useMemo } from 'react';
import MapleMarketAppCard from '@/components/maple-market/MapleMarketAppCard';
import MapleMarketCategoryBar from '@/components/maple-market/MapleMarketCategoryBar';
import MapleMarketFooter from '@/components/maple-market/MapleMarketFooter';
import MapleMarketHeroSection from '@/components/maple-market/MapleMarketHeroSection';
import MapleMarketNavbar, { MAPLE_MARKET_NAV_TOTAL_HEIGHT } from '@/components/maple-market/MapleMarketNavbar';
import type { MapleMarketCategory } from '@/data/mapleMarketApps';
import type { MapleMarketInfoPageId } from '@/components/maple-market/MapleMarketInfoView';
import { useMapleMarketLicenses } from '@/hooks/supabase/useMapleMarketLicenses';
import { useMarketplaceProducts } from '@/hooks/supabase/useMarketplaceProducts';

interface MapleMarketStoreViewProps {
  embedded?: boolean;
  isAdminAvailable?: boolean;
  onOpenAdmin?: () => void;
  onOpenLibrary?: () => void;
  activeCategory: MapleMarketCategory | 'All';
  onCategoryChange: (category: MapleMarketCategory | 'All') => void;
  onSelectApp: (appId: string) => void;
  onOpenPage?: (page: MapleMarketInfoPageId) => void;
}

export default function MapleMarketStoreView({
  embedded = false,
  isAdminAvailable = false,
  onOpenAdmin,
  onOpenLibrary,
  activeCategory,
  onCategoryChange,
  onSelectApp,
  onOpenPage,
}: MapleMarketStoreViewProps) {
  const { products, storeProducts, featuredProducts, categories, loading } = useMarketplaceProducts();
  const ownedSkus = useMapleMarketLicenses(products.map((product) => product.sku));

  const filteredApps = useMemo(() => {
    if (activeCategory === 'All') {
      return storeProducts;
    }
    return storeProducts.filter((app) => app.category === activeCategory);
  }, [activeCategory, storeProducts]);

  const topRated = useMemo(
    () => [...storeProducts].sort((a, b) => b.rating - a.rating).slice(0, 4),
    [storeProducts]
  );

  const contentSpacing = embedded ? 'pt-6' : undefined;
  const rootClassName = embedded ? 'min-h-full bg-background' : 'min-h-screen bg-background';
  const showHero = activeCategory === 'All';

  const hero = featuredProducts[0];

  return (
    <div className={rootClassName}>
      <MapleMarketNavbar
        products={storeProducts}
        embedded={embedded}
        isAdminAvailable={isAdminAvailable}
        onOpenAdmin={onOpenAdmin}
        onOpenLibrary={onOpenLibrary}
        onSelectCategory={onCategoryChange}
        onSelectApp={onSelectApp}
      />

      <main
        className={`container pb-8 ${contentSpacing ?? ''}`}
        style={embedded ? undefined : { paddingTop: `calc(${MAPLE_MARKET_NAV_TOTAL_HEIGHT} + 1.5rem)` }}
      >
        {showHero && hero && (
          <div className="mb-10 animate-fade-in">
            <MapleMarketHeroSection hero={hero} onSelectApp={onSelectApp} />
          </div>
        )}

        {showHero && topRated.length > 0 && (
          <section className="mb-10">
            <h2 className="mb-4 text-lg font-bold text-foreground">Top Rated</h2>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {topRated.map((app, index) => (
                <div key={app.id} className="animate-fade-in" style={{ animationDelay: `${index * 80}ms` }}>
                  <MapleMarketAppCard
                    app={app}
                    owned={ownedSkus.has(app.sku)}
                    variant="wide"
                    onSelectApp={onSelectApp}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-lg font-bold text-foreground">
              {activeCategory === 'All' ? 'Browse All' : activeCategory}
            </h2>
            {loading && <span className="text-sm text-muted-foreground">Syncing marketplace…</span>}
          </div>
          <MapleMarketCategoryBar active={activeCategory} categories={categories} onChange={onCategoryChange} />
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredApps.map((app, index) => (
              <div key={app.id} className="animate-fade-in" style={{ animationDelay: `${index * 60}ms` }}>
                <MapleMarketAppCard app={app} owned={ownedSkus.has(app.sku)} onSelectApp={onSelectApp} />
              </div>
            ))}
          </div>
          {filteredApps.length === 0 && (
            <div className="py-20 text-center text-muted-foreground">
              No apps found in this category yet.
            </div>
          )}
        </section>
      </main>
      <MapleMarketFooter onSelectCategory={onCategoryChange} onOpenPage={onOpenPage} />
    </div>
  );
}